// Investigate RX "Checked / Skip and Continue" queue, kept in sessionStorage per rx date:
//   checked_rx_numbers[rxdate]  - Rx checked in this tab
//   skipped_rx_numbers[rxdate]  - Rx skipped in this tab, in skip order (reviewed last)
//   skippedrx[rxdate]           - the queue itself (next Rx is the first entry)

const toRxList = (list) => (Array.isArray(list) ? list.map(String) : [])

const readSession = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key)) || {}
    } catch (error) {
        return {}
    }
}

export const readInvestigationQueueSession = (rxdate) => ({
    checked: toRxList(readSession('checked_rx_numbers')[rxdate]),
    skipped: toRxList(readSession('skipped_rx_numbers')[rxdate]),
    queue: toRxList(readSession('skippedrx')[rxdate]),
})

export const writeInvestigationQueueSession = (rxdate, { checked, skipped, queue }) => {
    const update = (key, value) => {
        if (value === undefined) return
        const stored = readSession(key)
        stored[rxdate] = toRxList(value)
        sessionStorage.setItem(key, JSON.stringify(stored))
    }
    update('checked_rx_numbers', checked)
    update('skipped_rx_numbers', skipped)
    update('skippedrx', queue)
}

/**
 * Order of Rx still to review: pending Rx that were not checked or skipped first, then the
 * skipped Rx (in skip order) once nothing else is left. Checked Rx never come back, and when a
 * fresh server list is given, Rx completed elsewhere are dropped too.
 *
 * @param {Array} pendingRxNumbers - not-investigated Rx for the date, from the server
 * @param {Array} checkedRxNumbers - Rx checked in this tab
 * @param {Array} skippedRxNumbers - Rx skipped in this tab, oldest first
 * @param {string|number} excludeRx - Rx to leave out (the one being left)
 */
export const buildInvestigationQueue = ({ pendingRxNumbers, checkedRxNumbers, skippedRxNumbers, excludeRx }) => {
    const pending = [...new Set(toRxList(pendingRxNumbers))]
    const pendingSet = new Set(pending)
    const checked = new Set(toRxList(checkedRxNumbers))
    const skipped = toRxList(skippedRxNumbers)
    const skippedSet = new Set(skipped)
    const exclude = excludeRx == null ? null : String(excludeRx)

    const isOpen = (rx) => rx !== exclude && !checked.has(rx)

    const notSkipped = pending.filter(rx => isOpen(rx) && !skippedSet.has(rx))
    if (notSkipped.length > 0) return notSkipped

    return [...new Set(skipped)].filter(rx => isOpen(rx) && pendingSet.has(rx))
}
